import React, { useState, useEffect, useCallback, useRef } from "react";
import { getAllCompanies } from "../../services/companyService";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import useThrottledScroll from "../../hooks/useThrottledScroll";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import CompaniesTable from "./components/Table/CompaniesTable";
import CompanyFormModal from "./components/Modal/CompanyFormModal";
import {
  SCROLL_THRESHOLD_PIXELS,
  SCROLL_THROTTLE_DELAY,
  ITEMS_PER_PAGE,
  DEBOUNCE_DELAY,
} from "./constants/companyConstants";
import "./Companies.scss";

/**
 * Companies Component
 * Main component for managing companies - create and view
 * Uses modal-based approach similar to Users component with infinite scroll
 */
const Companies = ({ admin, isSuperAdmin, userInfo, permissions = {} }) => {
  // Extract company permissions
  const canCreateCompany = permissions?.companies?.create === true;
  const canViewCompany = permissions?.companies?.view === true;

  // Ant Design message hook
  const messageApi = useMessageApi();

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'

  useBodyScrollLock(modalVisible);

  // Ref to track initial load (prevents double API call in React Strict Mode)
  const hasInitiallyLoaded = useRef(false);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const isClearingSearch = useRef(false);
  const fetchCompaniesRef = useRef(null);

  /**
   * Fetches companies with pagination, search, and sorting
   * @param {boolean} resetData - If true, resets to page 1 and replaces companies
   * @param {string} search - Search term to filter companies (optional)
   * @param {string} sortOrderOverride - Override sort order (optional, uses state if not provided)
   */
  const fetchCompanies = useCallback(
    async (resetData = false, search = null, sortOrderOverride = null) => {
      try {
        if (resetData) {
          setIsLoading(true);
          // Clear companies array immediately to show skeleton on refresh
          setCompanies([]);
          setPage(1);
        } else {
          setIsLoadingMore(true);
        }

        // Add 1 second delay to show skeleton loader
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const currentPage = resetData ? 1 : page;
        const searchParam = search !== null ? search : activeSearch;
        // Use override if provided, otherwise use state value
        const currentSortOrder =
          sortOrderOverride !== null ? sortOrderOverride : sortOrder;
        const result = await getAllCompanies(
          {
            page: currentPage,
            limit: ITEMS_PER_PAGE,
            search: searchParam || undefined,
            sortOrder: currentSortOrder,
          },
          messageApi
        );

        if (result && result.companies) {
          const newCompanies = result.companies;
          const total = result.total || 0;

          setTotalCount(total);

          if (resetData) {
            // Initial load or reset - replace companies
            setCompanies(newCompanies);
            setPage(2);
            setHasMore(newCompanies.length < total);
          } else {
            // Loading more - append companies
            setCompanies((prev) => {
              const loadedCount = prev.length + newCompanies.length;
              setHasMore(loadedCount < total);
              return [...prev, ...newCompanies];
            });
            setPage((prev) => prev + 1);
          }
        } else {
          if (resetData) {
            setCompanies([]);
            setTotalCount(0);
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        if (messageApi) {
          messageApi.error("Failed to fetch companies. Please try again.", 3);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [page, messageApi, activeSearch, sortOrder]
  );

  // Keep ref updated with latest fetchCompanies function
  useEffect(() => {
    fetchCompaniesRef.current = fetchCompanies;
  }, [fetchCompanies]);

  /**
   * Refreshes companies list (resets to page 1)
   * Clears search if needed
   */
  const refreshCompanies = useCallback(() => {
    isClearingSearch.current = true;
    setSearchTerm("");
    setActiveSearch("");
    fetchCompanies(true, "");
  }, [fetchCompanies]);

  /**
   * Loads more companies (for infinite scroll)
   * @param {boolean} shouldScroll - If true, scrolls to bottom after loading (only for button click)
   */
  const loadMoreCompanies = useCallback(
    async (shouldScroll = false) => {
      if (!isLoadingMore && hasMore && !isLoading) {
        await fetchCompanies(false);
        // Scroll is handled by the component (Table.jsx pattern)
      }
    },
    [isLoadingMore, hasMore, isLoading, fetchCompanies]
  );

  /**
   * Detects when user scrolls near bottom of page
   * Triggers loading more companies for infinite scroll
   * Does NOT scroll - maintains current scroll position (Table.jsx pattern)
   */
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    // Load more when user is near bottom (defined by SCROLL_THRESHOLD_PIXELS)
    // Pass false to shouldScroll - don't scroll, maintain position
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PIXELS) {
      loadMoreCompanies(false);
    }
  }, [isLoadingMore, hasMore, isLoading, loadMoreCompanies]);

  // Use throttled scroll handler to improve performance
  const throttledScroll = useThrottledScroll(
    handleScroll,
    SCROLL_THROTTLE_DELAY
  );

  /**
   * Opens modal for creating a new company
   * Checks permission before allowing action
   */
  const handleCreateCompany = useCallback(() => {
    if (!canCreateCompany) {
      console.warn("User does not have permission to create companies");
      return;
    }
    setModalVisible(true);
  }, [canCreateCompany]);

  /**
   * Closes the company form modal
   */
  const handleModalClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  /**
   * Handles successful company creation
   * Refreshes company list
   */
  const handleModalSuccess = useCallback(() => {
    refreshCompanies();
  }, [refreshCompanies]);

  /**
   * Handles search term change from CompaniesTable
   * Updates local search state
   */
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  /**
   * Handles sort order change
   * Toggles between 'asc' and 'desc' and refreshes data
   */
  const handleSortChange = useCallback(() => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    // Reset to page 1 and refresh data when sort changes
    // Pass the new sort order directly to fetchCompanies
    if (fetchCompaniesRef.current) {
      fetchCompaniesRef.current(true, activeSearch || null, newOrder);
    }
  }, [sortOrder, activeSearch]);

  /**
   * Debounced search effect
   * Triggers backend search after user stops typing
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isClearingSearch.current) {
      isClearingSearch.current = false;
      return;
    }

    clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      const trimmedSearch = searchTerm.trim();
      setActiveSearch(trimmedSearch);
      // Reset to page 1 when search changes
      // Use ref to avoid dependency issues
      if (fetchCompaniesRef.current) {
        fetchCompaniesRef.current(true, trimmedSearch);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm]); // Only depend on searchTerm, not fetchCompanies

  /**
   * Load companies on initial mount
   * Resets to page 1
   * Uses ref to prevent double API call in React Strict Mode (development)
   */
  useEffect(() => {
    if (!hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      fetchCompanies(true);
    }
  }, []);

  /**
   * Set up scroll listener for infinite scroll
   * Cleans up listener on unmount
   */
  useEffect(() => {
    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [throttledScroll]);

  // If user doesn't have view permission, show no access message
  if (!canViewCompany) {
    return (
      <div
        className="companies-module"
        style={{ padding: "40px", textAlign: "center" }}
      >
        <h3>No Access</h3>
        <p>
          You don't have permission to view the Companies module. Please contact
          your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="companies-module">
      {/* Companies Table */}
      <CompaniesTable
        companies={companies}
        loading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        totalCompanies={totalCount}
        canCreate={canCreateCompany}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onRefresh={refreshCompanies}
        onLoadMore={(shouldScroll) => loadMoreCompanies(shouldScroll)}
        onCreateCompany={handleCreateCompany}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />

      {/* Company Form Modal for Create Operations */}
      <CompanyFormModal
        visible={modalVisible}
        editingCompany={null}
        canCreate={canCreateCompany}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Companies;
